import { useState } from "react";
import { useCurrentAccount } from "@mysten/dapp-kit";
import { Ed25519Keypair } from "@mysten/sui/keypairs/ed25519";
import { provisionAgentMulti } from "@altheia-xyz/sui";
import { useOwnerExecutor } from "./suiExecutor";
import { api } from "./api";
import { SUI_DEPLOY, CORE_LATEST, AGENT_GAS, policyToConfig, buildRefs, saveAgent } from "./suiConfig";
import type { Policy } from "@/components/SuiPolicyEditor";

export type ProvisionStatus = "idle" | "funding" | "provisioning" | "recording" | "done" | "error";

/**
 * Mint flow (in-browser, owner-signed): generate the agent keypair, top it up
 * with gas, run provisionAgent (4-6 wallet signatures: vault, deposit, policy,
 * action params, agent cap), record to the backend, and persist the OwnerCap +
 * agent secret locally for revoke and the agent's own swaps. Returns the new
 * agent id for navigation, or null on failure.
 */
export function useProvisionAgent(token: string) {
  const account = useCurrentAccount();
  const ownerExec = useOwnerExecutor();
  const [status, setStatus] = useState<ProvisionStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  // The agent secret is shown ONCE here, never persisted (D2). Held in memory
  // only for the one-time credentials modal, then cleared.
  const [creds, setCreds] = useState<{ address: string; secret: string; agentId: string } | null>(null);

  async function provision(policy: Policy): Promise<string | null> {
    if (!account) {
      setError("connect a wallet first");
      setStatus("error");
      return null;
    }
    setError(null);
    try {
      const agent = Ed25519Keypair.generate();
      const agentAddress = agent.getPublicKey().toSuiAddress();
      const agentSecret = agent.getSecretKey();

      // ONE wallet signature: deposit + vault + policy + swap-guard + agent cap
      // + agent gas, composed in a single PTB against the upgraded core.
      setStatus("provisioning");
      const { cfg, fundAmount, quoteType, extraCaps } = policyToConfig(policy);
      const prov = await provisionAgentMulti(ownerExec, {
        corePkg: CORE_LATEST,
        quoteType,
        agentAddr: agentAddress,
        ownerAddr: account.address,
        fundAmount,
        agentGas: AGENT_GAS,
        cfg,
        extraCaps,
      });

      // 3. persist refs + secrets (OwnerCap + agent key live only here).
      // Key under the label now (recovery if register fails) and under the
      // backend id after register (the detail page loads control keys by it).
      const refs = buildRefs(prov, account.address);
      const displayPolicy = {
        per_asset_caps: policy.caps.map((c) => ({ asset: c.coin, per_day: c.per_day, per_tx: c.per_tx ?? null })),
        allowed_actions: policy.allowed_actions,
        expires_at: new Date(Date.now() + policy.expiry_hours * 3600_000).toISOString(),
      };
      saveAgent({ agentId: policy.name, agentAddress, refs, policy: displayPolicy, name: policy.name });

      // 4. record to the backend so the fleet + audit poller see it
      setStatus("recording");
      const res = await api.registerSuiAgent(
        {
          chain: "sui",
          name: policy.name,
          framework: "custom",
          agent_id: policy.name,
          sui_vault_id: prov.vault,
          sui_policy_id: prov.policy,
          sui_agent_cap_id: prov.cap,
          agent_address: agentAddress,
          policy: displayPolicy,
        },
        token,
      );
      saveAgent({ agentId: res.agent.id, agentAddress, refs, policy: displayPolicy, name: policy.name });
      setCreds({ address: agentAddress, secret: agentSecret, agentId: res.agent.id });
      setStatus("done");
      return res.agent.id;
    } catch (e) {
      setError(String((e as Error).message ?? e).slice(0, 200));
      setStatus("error");
      return null;
    }
  }

  function reset() {
    setStatus("idle");
    setError(null);
  }

  return { provision, status, error, reset, creds, clearCreds: () => setCreds(null) };
}
